import last from 'lodash/last';
import type { Observable as ObservableType } from 'rxjs/Observable';
import type { Issue } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import type { FieldConfigs } from '@atlassian/jira-software-backlog/src/common/hooks/use-store-fields/types.tsx';
import type { Card, CardId } from '../../model/types';
import type { CardCreateGql } from '../../rest/card-create-multiple-gql/types';
import type { EpicIssueTypeState } from '../../state/reducers/config/epic/types';
import { getEpicIssueType } from '../../state/selectors/app';
import {
	epicLinkShortCustomFieldIdSelector,
	sprintShortCustomFieldIdSelector,
} from '../../state/selectors/config';
import {
	getAppliedAssigneeFilters,
	getAppliedLabelFilters,
	getAppliedVersionFilters,
} from '../../state/selectors/filters';
import { filteredParentsSelector } from '../../state/selectors/issue-parent';
import { jqlContextSelector } from '../../state/selectors/jql-filter';
import { makeIsSprintCardList } from '../../state/selectors/sprints';
import type { MiddlewareAPI, State } from '../../state/types';
import type { ServiceContext } from '../service-context';
import { createMultipleCardService } from './card-create-multiple-gql';
import { inlineCardCreateService } from './card-inline-create-rest';

const createOverrides = (state: State, cardListId: string) => {
	const isSprintCardList = makeIsSprintCardList(state);
	const epicLinkShortCustomFieldId = epicLinkShortCustomFieldIdSelector(state);
	const sprintShortCustomFieldId = sprintShortCustomFieldIdSelector(state);

	const sprintId = isSprintCardList(cardListId) ? Number(cardListId) : undefined;
	const issueParent = last(filteredParentsSelector(state));
	const assignee = last(getAppliedAssigneeFilters(state));
	const version = last(getAppliedVersionFilters(state));
	const label = last(getAppliedLabelFilters(state));

	return {
		...(assignee ? { assignee } : {}),
		...(sprintId && sprintShortCustomFieldId ? { [sprintShortCustomFieldId]: sprintId } : {}),
		...(issueParent && epicLinkShortCustomFieldId
			? { [epicLinkShortCustomFieldId]: issueParent.key }
			: {}),
		...(version ? { fixVersion: version } : {}),
		...(label ? { labels: label } : {}),
	};
};

export function cardCreateService(
	serviceContext: ServiceContext,
	store: MiddlewareAPI,
	summaries: string[],
	issueTypeId: number,
	parentId: number | null,
	fixVersions: string[],
	cardListId: string,
	rankBeforeCardId: CardId | null = null,
	storeFieldsFromCardCreateData: (
		cardCreateData: CardCreateGql,
		fieldConfigs: FieldConfigs,
	) => void,
	storeFieldsFromFetchIssuesData: (
		issues: Issue[] | null,
		epicIssueType: EpicIssueTypeState | null,
	) => void,
): ObservableType<Card[] | null> {
	if (serviceContext.isCompanyManaged) {
		const state = store.getState();
		const contexts = jqlContextSelector(state);
		const epicIssueType = getEpicIssueType(state);
		const overrides = createOverrides(state, cardListId);

		return inlineCardCreateService(serviceContext, {
			summary: summaries[0],
			issueTypeId: String(issueTypeId),
			contexts,
			overrides,
			epicIssueType,
			storeFieldsFromFetchIssuesData,
		});
	}
	return createMultipleCardService(
		store,
		summaries,
		issueTypeId,
		parentId,
		fixVersions,
		cardListId,
		rankBeforeCardId,
		storeFieldsFromCardCreateData,
	);
}
